import { useEffect, useState, type ReactNode } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import TwivaMark from '@/components/feature/TwivaMark';
import { publicApi } from '@/utils/api';
import CreatorAvatars from './CreatorAvatars';
import { hasPhotos, type WorkingCreators, showCreatorCount } from './workingCreators';
import CreatorFlow, { type FeaturedCampaign } from './CreatorFlow';
import AppDownloadButtons from '@/components/feature/AppDownloadButtons';
import { JourneyCard, RotatingWords, type JourneyStage } from '@/components/motion/Motion';
import { stagger, useCountUp, useSpotlight } from '@/components/motion/motionHelpers';
import { PLATFORM } from '@/config/platform';

/** `GET /join/agencies/:ref`: the agency behind a join link. */
interface JoinAgency {
  ref: string;
  name: string;
  tagline: string;
  logo: string | null;
  logoInitials: string;
  colour: string | null;
  openCampaigns: number;
  /** Creators working with the agency, shown under its name. */
  creators: WorkingCreators;
  featured: FeaturedCampaign | null;
}

/**
 * An agency's public join page for creators (`/join/:ref`, optionally `?campaign=<link token>` to
 * feature that campaign): sign up or sign in, then join the agency's campaigns (CreatorFlow).
 */
export default function JoinAgencyPage() {
  const { ref = '' } = useParams();
  const [searchParams] = useSearchParams();
  const campaignToken = searchParams.get('campaign') ?? '';
  const [agency, setAgency] = useState<JoinAgency | null>(null);
  const [loadError, setLoadError] = useState('');
  const [compact, setCompact] = useState(false);

  useEffect(() => {
    const query = campaignToken ? `?campaign=${encodeURIComponent(campaignToken)}` : '';
    publicApi<JoinAgency>(`/join/agencies/${encodeURIComponent(ref)}${query}`)
      .then(setAgency)
      .catch((error: unknown) => setLoadError(error instanceof Error ? error.message : 'This join link could not be opened.'));
  }, [ref, campaignToken]);

  useEffect(() => {
    if (agency) document.title = `Join ${agency.name} on ${PLATFORM.name}`;
  }, [agency]);

  if (loadError || !agency) {
    return (
      <Frame>
        <div className="rounded-xl border border-background-200 bg-background-50 p-8 text-center">
          {loadError ? (
            <>
              <TwivaMark className="mx-auto mb-4 h-10 w-10" />
              <h1 className="font-heading text-lg font-semibold text-foreground-950">We couldn't open this link</h1>
              <p className="mt-2 text-sm text-foreground-600">{loadError}</p>
            </>
          ) : (
            <p className="text-sm text-foreground-600">Loading…</p>
          )}
        </div>
      </Frame>
    );
  }

  return (
    <div className="twiva-brand flex min-h-screen flex-col bg-background-100 lg:flex-row">
      <JoinPanel agency={agency} />

      {/* On phones the form is a sheet pulled up over the bottom of the burgundy hero. */}
      <main className="relative z-10 -mt-8 flex flex-1 flex-col rounded-t-[28px] bg-background-100 px-4 pb-8 pt-7 shadow-[0_-10px_30px_rgba(34,33,35,0.12)] sm:px-8 lg:mt-0 lg:rounded-none lg:py-12 lg:shadow-none">
        <div className="flex flex-1 items-start justify-center lg:items-center">
          <div className="w-full max-w-xl animate-fade-rise">
            <div className="overflow-hidden rounded-xl border border-background-200 bg-background-50 shadow-sm">
              <AgencyHeader agency={agency} compact={compact} />
              <div className="px-6 pb-6 pt-5 sm:px-8">
                <CreatorFlow
                  base={`/join/agencies/${encodeURIComponent(agency.ref)}`}
                  agencyName={agency.name}
                  featured={agency.featured}
                  onStepChange={(step) => setCompact(step !== 'choose')}
                  intro={
                    <>
                      {agency.featured ? (
                        <div className="join-invite mb-4 flex items-start gap-3 rounded-lg border border-accent-200 bg-accent-50 px-4 py-3">
                          <i className="ri-mail-star-line join-invite-icon mt-0.5 text-lg text-accent-700" aria-hidden="true" />
                          <p className="text-sm text-accent-900">
                            You've been invited to <span className="font-semibold">{agency.featured.name}</span>. Sign in or create your
                            account to join it.
                          </p>
                        </div>
                      ) : null}
                      <p className="text-sm text-foreground-700">
                        {agency.name} works with creators on campaigns for brands. Create your free {PLATFORM.name} creator account, or sign in if you
                        already have one, then choose the campaigns you'd like to join.
                      </p>
                      {agency.openCampaigns > 0 ? <OpenCampaigns count={agency.openCampaigns} /> : null}
                    </>
                  }
                />
              </div>
            </div>
          </div>
        </div>
        {/* Phones and tablets only: the Twiva app, just above "Powered by Twiva". */}
        <AppDownloadButtons className="mx-auto mt-10 w-full max-w-sm" />
        <p className="mt-6 flex items-center justify-center gap-2 text-xs text-foreground-500">
          Powered by
          <img src={PLATFORM.wordmark} alt={PLATFORM.name} className="h-4 w-auto" />
        </p>
      </main>
    </div>
  );
}

/** "3 campaigns are open to new creators right now", with a live dot and the number counting up. */
function OpenCampaigns({ count }: { count: number }) {
  const shown = useCountUp(count, 900);
  return (
    <p className="mt-2 flex items-center gap-2 text-xs font-medium text-foreground-500">
      <span className="relative flex h-2 w-2" aria-hidden="true">
        <span className="absolute inline-flex motion-safe:animate-ping h-full w-full rounded-full bg-accent-400" />
        <span className="relative inline-flex h-2 w-2 rounded-full bg-accent-500" />
      </span>
      <span>
        <span className="tabular-nums">{shown}</span> {count === 1 ? 'campaign is' : 'campaigns are'} open to new creators right now.
      </span>
    </p>
  );
}

// Short phrases: the longest one sets the line's width, and the agency's name already makes it long.
const JOIN_WORDS = ['Reels.', 'TikToks.', 'podcasts.', 'stories.', 'videos.'];

const JOIN_POINTS = [
  { icon: 'ri-user-star-line', text: `One free ${PLATFORM.name} account, the same as in the app` },
  { icon: 'ri-megaphone-line', text: "Join open campaigns and the ones you're invited to" },
  { icon: 'ri-hand-coin-line', text: `Get paid straight to your ${PLATFORM.walletName}` },
];

/** The agency's burgundy panel: who they are, what joining gets you, and their creators. */
function JoinPanel({ agency }: { agency: JoinAgency }) {
  const spotlight = useSpotlight();
  const creatorCount = useCountUp(agency.creators.total);
  const lead = `Join ${agency.name} and get paid for your`;
  const stages: JourneyStage[] = [
    { icon: 'ri-mail-star-line', label: `Invited by ${agency.name}` },
    { icon: 'ri-checkbox-circle-line', label: 'Joined in one tap' },
    { icon: 'ri-film-line', label: 'Content approved' },
    { icon: 'ri-hand-coin-line', label: `Paid to your ${PLATFORM.walletName}` },
  ];
  return (
    <aside
      ref={spotlight.ref}
      onPointerMove={spotlight.onPointerMove}
      onPointerLeave={spotlight.onPointerLeave}
      className="brand-gradient relative overflow-hidden px-5 pb-16 pt-6 text-background-50 sm:px-10 lg:flex lg:w-[44%] lg:flex-col lg:justify-between lg:py-12 xl:px-16"
    >
      <div className="join-aurora left-[-10%] top-[20%] h-72 w-72 bg-secondary-500/40 lg:h-96 lg:w-96" aria-hidden="true" />
      <div className="join-aurora is-second bottom-[-5%] right-[-15%] h-64 w-64 bg-primary-300/25 lg:h-80 lg:w-80" aria-hidden="true" />
      <div className="join-spotlight" aria-hidden="true" />
      <div
        data-audience="creator"
        className="join-shapes pointer-events-none absolute -right-10 -top-10 grid grid-cols-2 gap-2 opacity-15 lg:-right-16 lg:-top-16 lg:gap-3"
        aria-hidden="true"
      >
        <span className="h-16 w-16 rounded-xl bg-background-50 lg:h-28 lg:w-28 lg:rounded-2xl" />
        <span className="h-16 w-16 rounded-xl bg-background-50/60 lg:h-28 lg:w-28 lg:rounded-2xl" />
        <span className="h-16 w-16 rounded-xl bg-background-50/40 lg:h-28 lg:w-28 lg:rounded-2xl" />
        <span className="h-16 w-16 rounded-xl bg-background-50/80 lg:h-28 lg:w-28 lg:rounded-2xl" />
      </div>

      {/* The agency first, then Twiva. */}
      <div className="relative flex items-center gap-3">
        <AgencyLogo agency={agency} size="sm" />
        <span className="font-heading text-base font-semibold">{agency.name}</span>
        <span className="text-xs text-background-50/60">on</span>
        <img src={PLATFORM.wordmarkWhite} alt={PLATFORM.name} className="h-5 w-auto" />
      </div>

      <div className="join-cascade relative mt-7 lg:mt-8">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-background-50/70">For creators</p>
        <h2
          className="mt-2 max-w-md font-heading text-[28px] font-semibold leading-tight sm:text-4xl lg:mt-3"
          aria-label={`${lead} ${JOIN_WORDS[0]}`}
        >
          {lead.split(' ').map((word, index) => (
            <span key={`${word}-${index}`} aria-hidden="true">
              <span className="join-word" style={stagger(index)}>
                {word}
              </span>{' '}
            </span>
          ))}
          <span className="join-word" style={stagger(lead.split(' ').length)}>
            <RotatingWords words={JOIN_WORDS} delay={1400} />
          </span>
        </h2>
        {agency.tagline ? <p className="mt-3 max-w-md text-sm italic text-background-50/75">“{agency.tagline}”</p> : null}
        <ul className="mt-5 hidden space-y-4 sm:block lg:mt-8">
          {JOIN_POINTS.map((point) => (
            <li key={point.text} className="flex items-center gap-3 text-sm text-background-50/90 lg:text-base">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-background-50/15">
                <i className={point.icon} aria-hidden="true" />
              </span>
              {point.text}
            </li>
          ))}
        </ul>
      </div>

      {/* Desktop: an example campaign with this agency, ticking through its stages. */}
      <div className="relative mt-10 hidden animate-fade-rise lg:block">
        <JourneyCard
          title={agency.featured?.name ?? `A campaign with ${agency.name}`}
          subtitle="Example · from invite to payout"
          stages={stages}
        />
      </div>

      {hasPhotos(agency.creators) ? (
        <div className="relative mt-6 flex items-center gap-3 lg:mt-10 lg:gap-4">
          <CreatorAvatars creators={agency.creators} ring="border-primary-700" />
          <p className="max-w-[16rem] text-xs leading-snug text-background-50/85 lg:text-sm">
            {showCreatorCount(agency.creators) ? (
              <>
                <span className="font-semibold tabular-nums text-background-50">{creatorCount}</span> creators work
              </>
            ) : (
              'Creators work'
            )}{' '}
            with {agency.name}
          </p>
        </div>
      ) : null}
    </aside>
  );
}

function Frame({ children }: { children: ReactNode }) {
  return (
    <div className="twiva-brand flex min-h-screen items-start justify-center bg-background-100 px-4 py-8 sm:items-center sm:py-12">
      <div className="w-full max-w-xl">
        {children}
        <p className="mt-6 flex items-center justify-center gap-2 text-xs text-foreground-500">
          Powered by
          <img src={PLATFORM.wordmark} alt={PLATFORM.name} className="h-4 w-auto" />
        </p>
      </div>
    </div>
  );
}

function AgencyLogo({ agency, size }: { agency: JoinAgency; size: 'sm' | 'lg' }) {
  const box = size === 'lg' ? 'h-16 w-16 text-xl' : 'h-9 w-9 text-sm';
  return agency.logo ? (
    <img src={agency.logo} alt="" className={`${box} rounded-lg bg-background-50 object-contain`} />
  ) : (
    <span
      className={`${box} flex items-center justify-center rounded-lg bg-primary-600 font-heading font-semibold text-background-50`}
      style={agency.colour ? { backgroundColor: agency.colour } : undefined}
      aria-hidden="true"
    >
      {agency.logoInitials}
    </span>
  );
}

function AgencyHeader({ agency, compact }: { agency: JoinAgency; compact: boolean }) {
  if (compact) {
    return (
      <div className="flex items-center gap-3 border-b border-background-200 px-6 py-3 sm:px-8">
        <AgencyLogo agency={agency} size="sm" />
        <span className="font-heading text-sm font-semibold text-foreground-950">{agency.name}</span>
      </div>
    );
  }
  return (
    <div key="welcome" className="join-cascade border-b border-background-200 px-6 pb-5 pt-6 sm:px-8">
      <span className="page-rule" aria-hidden="true" />
      {/* The page's title for screen readers and tabs; the panel beside it carries the big headline. */}
      <h1 className="font-heading text-2xl font-semibold text-foreground-950">Let's get you started</h1>
      <p className="mt-1 text-sm text-foreground-600">Create your account or sign in, then pick the {agency.name} campaigns you want.</p>
    </div>
  );
}
